import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { isMobile } from '@/utils';
import { inject, observer } from 'mobx-react';
import { PaymentTracker } from '@/utils/Tracker/index';
import { enablePaypal } from '@/utils/configs/payment.config';

/**
 *
 * @description 对满足条件的进行过滤，不显示Msg
 */
function filter(props, component) {
  const {
    area: {
      country: { code, currency, language },
    },
    app: { isProductPage, isCartPage, isOrderConfirmPage, productRepairStockList = [], isFilterRepairStock },
    amount: propsAmount,
  } = props;

  let enabled = enablePaypal({ code });

  if (enabled && isFilterRepairStock) enabled = false;

  if (!propsAmount || propsAmount <= 0) enabled = false;

  if (!enabled) return null;

  return <Index {...props} />;
}

/**
 * 渲染paylater标识
 * @param {number} amount 数量，数量变化会导致组件重渲染
 * @param {object} style 防止重渲染太慢，可设置display=none
 * @param {object} messageConfig 对paypal.message的配置(可配置onRender/onClick/style等)
 * @param id paypayMsg安置的位置，也是埋点的from
 * @description 移动端样式见https://paypal.github.io/react-paypal-js/?path=/docs/example-paypalmessages--default
 */
function Index(props) {
  const {
    messageConfig = {},
    style = {},
    area: {
      country: { code, currency, language },
    },
    auth,
    id = '',
    amount: propsAmount,
  } = props;
  const [loaded, setLoaded] = useState(false);

  const amount = useMemo(() => {
    const _amount = typeof propsAmount === 'string' ? Number(propsAmount.replace(/\$|,|\£|\€/g, '')) : propsAmount;
    return _amount.toFixed(2);
  }, [propsAmount]);

  /**
   * 反复查询window.paypal，避免js未下载完全的情况
   */
  function queryWindowPaypal() {
    return new Promise((resolve, reject) => {
      let counter = 0;
      const MAX_COUNTER = 30;
      const timer = setInterval(() => {
        const paylater = window.paypal;
        counter++;
        if (paylater) {
          resolve(true);
          clearInterval(timer);
        }
        if (counter > MAX_COUNTER) {
          resolve(false);
          clearInterval(timer);
        }
      }, 200);
    });
  }

  // 判断sdk是否下载完全,金额变动重新查询
  useEffect(() => {
    const query = async () => {
      try {
        if (window.paypal) {
          setLoaded(true);
        } else {
          const res = await queryWindowPaypal();

          setLoaded(res);
        }
      } catch (e) {
        auth.dataCollect({
          event: 'klarna.Messages.Error',
        });
      }
    };
    if (!loaded) {
      query();
    }
  }, [amount]);

  // 渲染Paypal Later支付提示 ，参数配置参考：https://developer.paypal.com/docs/business/pay-later/us/integrate/advanced-js-options/
  // 启用美英法地区，（官方设定）使用区间30 - 1500美元（paypal商户后台设置，无需开发处理）
  // 节流，首次触发，二次触发需节流，防止购物车页面短时间内来回变动amount导致渲染覆盖。
  const setPayPalLater = useCallback(() => {
    try {
      window.paypal
        ?.Messages?.({
          amount,
          buyerCountry: code,
          currency,
          placement: 'product', // home，category，product，cart，和payment
          ...messageConfig,
          style: {
            ...messageConfig.style,
            logo: {
              type: 'inline',
            },
          },
          onRender: () => {
            // such as send a "render event" to your own analytics platform.
            PaymentTracker.payLaterTracker({
              action: `show_PayLaterMsg${!!id ? '_' + id : ''}`,
              label: document?.location.pathname,
            });
            messageConfig.onRender?.();
          },
          onClick: () => {
            // such as send a "click event" to your own analytics platform.
            PaymentTracker.payLaterTracker({
              action: `click_PayLaterMsg${!!id ? '_' + id : ''}`,
              label: document?.location.pathname,
            });
            messageConfig.onClick?.();
          },
        })
        ?.render('#pp-message' + id);
    } catch (e) {
      auth.dataCollect({
        event: 'paypal.Messages.Error',
      });
    }
  }, [amount, auth, code, currency, id, messageConfig]);

  // 初始化setting，需确认paypal sdk是否下载完全
  useEffect(() => {
    if (loaded) {
      setPayPalLater();
    }
  }, [amount, loaded]);

  return <div id={'pp-message' + id} style={{ width: '100%', ...style }} />;
}

export default inject('area', 'auth', 'app', 'order')(observer((props) => filter(props, <Index />)));
